import React from 'react';
import { useTranslation } from '../../../context/LanguageContext';

const Pagination = ({ pagination, onPageChange, onPerPageChange = () => {} }) => {
    // Ensure we have a valid per_page value, defaulting to 10 if not provided
    const { links, current_page, last_page, from, to, total, per_page } = pagination;
    const perPageValue = per_page || 10;
    const { t, language } = useTranslation();
    const isRTL = language === 'ar';
    
    // Use the admin namespace since this is an admin component
    const translations = {
        previous: t('admin:previous'),
        next: t('admin:next'),
        showing: t('admin:showing'),
        to: t('admin:to'),
        of: t('admin:of'),
        results: t('admin:results'),
        rows_per_page: t('admin:rows_per_page')
    };
    
    // Available per page options
    const perPageOptions = [10, 25, 50, 100];
    
    // If perPageValue is not in the available options, add it to ensure the dropdown shows the current value
    if (perPageValue && !perPageOptions.includes(perPageValue)) {
        perPageOptions.push(perPageValue);
        // Sort numerically
        perPageOptions.sort((a, b) => a - b);
    }

    if (!pagination || !links || links.length === 0 || total === 0) {
        return null;
    }

    const handlePageClick = (page, event) => {
        event.preventDefault();
        if (page && page !== current_page) {
            onPageChange(page);
        }
    };

    const getPageFromUrl = (url) => {
        if (!url) return null;
        const match = url.match(/page=(\d+)/);
        return match ? parseInt(match[1]) : null;
    };
    
    const handlePerPageChange = (e) => {
        const perPage = parseInt(e.target.value, 10);
        onPerPageChange(perPage);
    };

    return (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
                {/* Mobile pagination - Reverse order for RTL */}
                {isRTL ? (
                    <>
                        <button
                            onClick={(e) => handlePageClick(current_page < last_page ? current_page + 1 : null, e)}
                            disabled={current_page >= last_page}
                            className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                                current_page >= last_page
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            {translations.next}
                        </button>
                        <button
                            onClick={(e) => handlePageClick(current_page > 1 ? current_page - 1 : null, e)}
                            disabled={current_page <= 1}
                            className={`mr-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                                current_page <= 1 
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            {translations.previous}
                        </button>
                    </>
                ) : (
                    <>
                        <button
                            onClick={(e) => handlePageClick(current_page > 1 ? current_page - 1 : null, e)}
                            disabled={current_page <= 1}
                            className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                                current_page <= 1 
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            {translations.previous}
                        </button>
                        <button
                            onClick={(e) => handlePageClick(current_page < last_page ? current_page + 1 : null, e)}
                            disabled={current_page >= last_page}
                            className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                                current_page >= last_page
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            {translations.next}
                        </button>
                    </>
                )}
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between flex-wrap">
                <div>
                    <p className={`text-sm text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                        {isRTL ? (
                            // Arabic style number format
                            <>
                                {translations.showing} <span className="font-medium">{from}</span> {translations.to}{' '}
                                <span className="font-medium">{to}</span> {translations.of}{' '}
                                <span className="font-medium">{total}</span> {translations.results}
                            </>
                        ) : (
                            // English style number format
                            <>
                                {translations.showing} <span className="font-medium">{from}</span> {translations.to}{' '}
                                <span className="font-medium">{to}</span> {translations.of}{' '}
                                <span className="font-medium">{total}</span> {translations.results}
                            </>
                        )}
                    </p>
                </div>
                <div className={`flex items-center mb-2 sm:mb-0 ${isRTL ? 'ml-4 rtl' : 'mr-4 ltr'}`} style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                    <span className={`text-sm text-gray-700 ${isRTL ? 'ml-2' : 'mr-2'}`}>{translations.rows_per_page}</span>
                    <select 
                        className={`border border-gray-300 rounded-md text-sm py-1 ${isRTL ? 'pl-8 pr-2' : 'pr-8 pl-2'} bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none`}
                        onChange={handlePerPageChange}
                        value={perPageValue}
                        style={{
                            backgroundImage: "url('data:image/svg+xml;charset=US-ASCII,<svg width=\"20\" height=\"20\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M7 7l3 3 3-3\" stroke=\"%23666\" stroke-width=\"1.5\" fill=\"none\" stroke-linecap=\"round\" /></svg>')",
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: isRTL ? '0.5rem 50%' : 'calc(100% - 0.5rem) 50%',
                            direction: isRTL ? 'rtl' : 'ltr'
                        }}
                    >
                        {perPageOptions.map(option => (
                            <option key={option} value={option} style={{ textAlign: isRTL ? 'right' : 'left' }}>{option}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <nav className={`relative z-0 inline-flex rounded-md shadow-sm ${isRTL ? 'space-x-reverse -space-x-px' : '-space-x-px'}`} aria-label="Pagination">
                        {links.map((link, index) => {
                            // Skip the "Previous" and "Next" links in desktop view
                            if (index === 0 || index === links.length - 1) {
                                const isFirst = index === 0;
                                const isDisabled = !link.url;
                                const page = getPageFromUrl(link.url);
                                
                                return (
                                    <button
                                        key={index}
                                        onClick={(e) => handlePageClick(page, e)}
                                        disabled={isDisabled}
                                        className={`relative inline-flex items-center px-2 py-2 ${
                                            isFirst ? (isRTL ? 'rounded-r-md' : 'rounded-l-md') : (isRTL ? 'rounded-l-md' : 'rounded-r-md')
                                        } border border-gray-300 bg-white text-sm font-medium ${
                                            isDisabled 
                                                ? 'text-gray-300 cursor-not-allowed' 
                                                : 'text-gray-500 hover:bg-gray-50'
                                        }`}
                                        aria-label={isFirst ? translations.previous : translations.next}
                                    >
                                        <span>{isFirst ? translations.previous : translations.next}</span>
                                    </button>
                                );
                            }
                            
                            // Regular page links
                            return (
                                <button
                                    key={index}
                                    onClick={(e) => handlePageClick(getPageFromUrl(link.url), e)}
                                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                        link.active
                                            ? 'z-10 bg-yellow-50 border-yellow-500 text-yellow-600'
                                            : link.url 
                                                ? 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                : 'bg-white border-gray-300 text-gray-700'
                                    }`}
                                    aria-label={`Page ${getPageFromUrl(link.url) || '#'}`}
                                    disabled={!link.url}
                                    aria-current={link.active ? 'page' : undefined}
                                >
                                    {getPageFromUrl(link.url) || link.label.replace(/&laquo;|&raquo;/g, '')}
                                </button>
                            );
                        })}
                    </nav>
                </div>
            </div>
        </div>
    );
};

export default Pagination;
